import { CONTENT } from '../../shared/constants/content';
import { CONTENT_DE } from '../../shared/constants/content_de';
import { Block, BLOCK_PLACEMENT_END } from '../model/blocks';
import { BuiltInPage, PAGE_SLOTS } from '../../configuration/pageSlots';
import { toBlocks } from '../../adapters/secondary/content/blockMapper';
import { Language } from '../../shared/constants/routes';

/**
 * The pages that are built in code and can carry editor-added sections.
 *
 * Taken from the section list rather than written out again, so that a page
 * cannot be added there and forgotten here.
 */
export type ExtendablePage = BuiltInPage;

/**
 * Validating the same JSON on every render would be wasted work: the content
 * is baked into the bundle and cannot change while the page is open.
 */
const extraBlockCache = new Map<string, Block[]>();

/**
 * Single entry point for page copy. Pages ask for the section they render
 * rather than reaching into the content modules, so swapping or splitting the
 * content source stays a change in one place.
 */
export class ContentUseCase {
  getAllContent(language: Language) {
    return language === 'de' ? CONTENT_DE : CONTENT;
  }

  getOrgInfo(language: Language) {
    return this.getAllContent(language).org;
  }

  getHomeContent(language: Language) {
    return this.getAllContent(language).home;
  }

  getOrganisationContent(language: Language) {
    return this.getAllContent(language).organisation;
  }

  getMitmachenContent(language: Language) {
    return this.getAllContent(language).mitmachen;
  }

  getSocialMediaContent(language: Language) {
    return this.getAllContent(language).socialMedia;
  }

  getPodcastContent(language: Language) {
    return this.getAllContent(language).podcast;
  }

  getLegalContent(language: Language) {
    return this.getAllContent(language).legal;
  }

  getCookieBanner(language: Language) {
    return this.getAllContent(language).legal.cookieBanner;
  }

  /**
   * Whether a built-in page is switched on in the editing interface.
   *
   * Absent means published, so a page can never disappear because a save
   * dropped the key - and the start page, which has no switch at all, is
   * always served. The flag is not translated: a page that exists in one
   * language and 404s in the other would be worse than either.
   */
  isPublished(page: ExtendablePage): boolean {
    const content = CONTENT_DE[page] as { published?: boolean };
    return content.published !== false;
  }

  /**
   * What a built-in page is called in the menu.
   *
   * Editorial, like the rest of a page's copy: the pages assembled at /admin
   * have carried their own `navLabel` from the start, and the five built in
   * code now do too, so one menu is edited in one way. Translated, because a
   * menu entry is text a visitor reads.
   *
   * Blank or missing falls back to the name in code - see BUILT_IN_NAV_ITEMS -
   * so an emptied field leaves a menu entry with a name rather than a gap
   * nobody can click by sight.
   */
  getNavLabel(page: ExtendablePage, language: Language): string | undefined {
    const content = this.getAllContent(language)[page] as { navLabel?: string };
    return content.navLabel?.trim() || undefined;
  }

  /**
   * Whether the dark view is offered at all.
   *
   * Absent means off, which is the opposite of what `isPublished` does above,
   * and deliberately so: the two switches fail in opposite directions. A page
   * that loses its key must not go offline, so absence there means "on". A
   * missing key here would turn on a view nobody has looked at since it was
   * switched off - and the logo and the goals are placed artwork set in the CI
   * anthracite (see Logo.tsx), so the dark view is currently the one that needs
   * a decision, not the one to fall into.
   *
   * Not translated: a site that offers the dark view in German and not in
   * English would be two different sites.
   */
  isDarkModeEnabled(): boolean {
    const display = CONTENT_DE.display as { darkMode?: boolean };
    return display.darkMode === true;
  }

  /**
   * Whether the language switcher is offered in the header.
   *
   * Absent means off, the same direction as `isDarkModeEnabled`: this hides a
   * control, and a control that reappears because a key went missing is worse
   * than one that stays hidden.
   *
   * It hides the control only. The English pages stay where they are, keep
   * their addresses and stay in the sitemap, so nothing that is already linked
   * or indexed breaks - and switching it back on is one tick.
   */
  isLanguageSwitcherEnabled(): boolean {
    const display = CONTENT_DE.display as { languageSwitcher?: boolean };
    return display.languageSwitcher === true;
  }

  /**
   * Whether the footer opens with the wordmark and the two contact symbols.
   *
   * All three now stand at the top of every page - the wordmark on the left of
   * the header, Instagram and the email address on the right - so the block is
   * off by default: the same three things twice on a short page is a
   * repetition, and the end of a page is not where either belongs.
   *
   * Off, not deleted. Whether a footer carries a wordmark is an editorial
   * decision, and this keeps it a tick in the CMS instead of a code change.
   * The legal line below it is unaffected and stays where it is either way.
   *
   * Absent means off, like the two switches above - a block that comes back on
   * its own because a key went missing is the wrong failure.
   */
  isFooterBrandingEnabled(): boolean {
    const display = CONTENT_DE.display as { footerBranding?: boolean };
    return display.footerBranding === true;
  }

  /**
   * The sections an editor added to a built-in page that belong in one slot.
   *
   * A slot is a section of the page, and a block placed "before" it renders
   * above it. A placement naming a slot this page does not have - a section
   * that was renamed, or content written before placements existed - falls to
   * the end rather than disappearing: a section in the wrong place can be
   * moved, one that never renders is invisible to the person who wrote it.
   */
  getExtraBlocksBefore(page: ExtendablePage, slot: string, language: Language): Block[] {
    const known = (PAGE_SLOTS[page] ?? []).map((entry) => entry.key);

    return this.getExtraBlocks(page, language).filter((block) => {
      const placement = known.includes(block.placement) ? block.placement : BLOCK_PLACEMENT_END;
      return placement === slot;
    });
  }

  /**
   * Every section an editor added to a built-in page, in the order they are
   * listed in the editing interface.
   */
  getExtraBlocks(page: ExtendablePage, language: Language): Block[] {
    const cacheKey = `${page}:${language}`;
    const cached = extraBlockCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const section = this.getAllContent(language)[page] as { extraBlocks?: unknown };
    // The editing interface writes the section *type* into the leading language
    // only, so a translated section is words with nothing saying what it is.
    // The German list fills those gaps, exactly as it does for a page built in
    // the CMS - without it a translated section is silently missing.
    const german = CONTENT_DE[page] as { extraBlocks?: unknown };
    const blocks = toBlocks(section.extraBlocks, german.extraBlocks);
    extraBlockCache.set(cacheKey, blocks);
    return blocks;
  }
}
